import { differenceInCalendarDays, format as formatDate } from "date-fns"

/**
 * The list table's pure parts: how a header click moves the sort, which colour a
 * name is drawn in, and how a date reads. Kept apart from the components so they
 * can be tested without a DOM.
 */

export type ListTableSortDirection = "asc" | "desc"

export interface ListTableSort {
  columnId: string
  direction: ListTableSortDirection
}

/**
 * What a click on a column heading asks for.
 *
 * A column is first asked for descending — the order a reader usually wants of a
 * date or a status — and after that each click flips it. There is no unsorted
 * step: the list always has an order, so clicking cannot take it away.
 */
export const nextListTableSort = (
  current: ListTableSort | null | undefined,
  columnId: string,
): ListTableSort => ({
  columnId,
  direction: current?.columnId === columnId && current.direction === "desc" ? "asc" : "desc",
})

/** The nine hues a name can be drawn in. Order matters: the hash indexes it. */
export const LIST_TABLE_SWATCHES = [
  "grey", "brown", "orange", "yellow", "green", "blue", "purple", "pink", "red",
] as const

export type ListTableSwatch = (typeof LIST_TABLE_SWATCHES)[number]

/**
 * The hue a name is drawn in, picked by the name itself so the same label or the
 * same person is the same colour on every surface and every visit.
 */
export const swatchFor = (name: string): ListTableSwatch => {
  let hash = 0

  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) >>> 0
  }

  return LIST_TABLE_SWATCHES[hash % LIST_TABLE_SWATCHES.length]
}

/**
 * A swatch as a CSS colour. `dot` is the hue at full voice, `soft` its palest
 * tint (an avatar's ground) and `ink` its darkest (the initial on that ground).
 */
export const swatchColor = (swatch: ListTableSwatch, tone: "dot" | "soft" | "ink" = "dot") =>
  `hsl(var(--v2-swatch-${swatch}${tone === "dot" ? "" : `-${tone}`}))`

/** The longest label name a chip carries before it says it has been cut. */
const LABEL_MAX_CHARS = 20

/** A label name held to what a chip will carry, with the ellipsis as part of it. */
export const clipLabel = (name: string, max = LABEL_MAX_CHARS) =>
  name.length > max ? `${name.slice(0, max - 1).trimEnd()}…` : name

/** A label name cut to a given number of characters, for a chip that has to give. */
export const shortenLabel = (name: string, chars: number) =>
  `${name.slice(0, chars).trimEnd()}…`

/** A calendar day with no time in it: `YYYY-MM-DD`. */
const BARE_DAY = /^\d{4}-\d{2}-\d{2}$/

/** The time of day, `9:41 AM`. Made once: a formatter with options is built per call otherwise. */
const TIME_OF_DAY = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" })

/**
 * A value as a calendar day in local time.
 *
 * A bare `YYYY-MM-DD` is read as the day it names rather than through `Date`,
 * which would take it as UTC midnight and show the day before anywhere west of
 * Greenwich. Anything with a time in it is a moment, and takes the local day.
 */
export const toLocalDay = (value: string | Date): Date => {
  if (typeof value === "string" && BARE_DAY.test(value)) {
    const [ year, month, day ] = value.split("-").map(Number)

    return new Date(year, month - 1, day)
  }

  const moment = new Date(value)

  return new Date(moment.getFullYear(), moment.getMonth(), moment.getDate())
}

/** Whole days from `today` to `value`, negative when it has passed. */
export const dayGap = (value: string | Date, today: Date = new Date()) =>
  differenceInCalendarDays(toLocalDay(value), toLocalDay(today))

/**
 * A date as a row shows it. Inside the year being read it is the day — `Aug 26`
 * — because the day is what you act on. Outside it, the month and the year —
 * `Mar 2027` — because at that distance the year is what you want to know.
 */
export const shortDate = (value: string | Date, today: Date = new Date()) => {
  const day = toLocalDay(value)

  return formatDate(day, day.getFullYear() === today.getFullYear() ? "MMM d" : "MMM yyyy")
}

const NEAR_DAYS: Record<number, string> = { [-1]: "Yesterday", 0: "Today", 1: "Tomorrow" }

export type DueState = "upcoming" | "today" | "late"

/**
 * Where a due date stands. A date that has passed on something already finished
 * is not a warning, so it stays `upcoming` rather than `late`.
 */
export const dueState = (value: string | Date, done = false, today: Date = new Date()): DueState => {
  const gap = dayGap(value, today)

  if (gap === 0) return "today"

  return gap < 0 && !done ? "late" : "upcoming"
}

/** A due date's words in the row: a name for the three nearest days, else the date. */
export const dueLabel = (value: string | Date, today: Date = new Date()) =>
  NEAR_DAYS[dayGap(value, today)] ?? shortDate(value, today)

const dayWords = (value: string | Date, today: Date, withYear: boolean) => {
  const day = toLocalDay(value)

  return formatDate(day, withYear && day.getFullYear() !== today.getFullYear() ? "MMM d, yyyy" : "MMM d")
}

/**
 * How far off a due date is, in the largest unit still true of it: days inside a
 * month, months inside a year, then years.
 */
export const dueDistance = (value: string | Date, done = false, today: Date = new Date()) => {
  const gap = dayGap(value, today)

  if (gap === 0) return "Due today"
  if (gap < 0 && done) return `Due on ${dayWords(value, today, true)}`

  const away = Math.abs(gap)
  let count: number
  let unit: string

  if (away < 30) {
    count = away
    unit = "day"
  } else if (away < 330) {
    count = Math.max(1, Math.round(away / 30))
    unit = "month"
  } else {
    count = Math.max(1, Math.round(away / 365))
    unit = "year"
  }

  return `${gap < 0 ? "Overdue by" : "Due in"} ${count} ${unit}${count === 1 ? "" : "s"}`
}

/**
 * A moment a reader can place: the time of day when it falls in the year being
 * read, the year when it does not.
 */
export const momentWords = (value: string | Date, today: Date = new Date()) => {
  // A bare day has no time of day to tell, and read through `Date` it would be
  // UTC midnight — the day before anywhere west of Greenwich.
  if (typeof value === "string" && BARE_DAY.test(value)) {
    return dayWords(value, today, true)
  }

  const moment = new Date(value)

  if (moment.getFullYear() !== today.getFullYear()) {
    return dayWords(moment, today, true)
  }

  return `${dayWords(moment, today, false)}, ${TIME_OF_DAY.format(moment)}`
}

/**
 * The first place a search query falls in a piece of text, as the text before
 * it, the match and the text after. Null when there is nothing to mark.
 */
export const splitMatch = (text: string, query: string | null | undefined) => {
  const needle = query?.trim().toLowerCase()

  if (!needle) return null

  const at = text.toLowerCase().indexOf(needle)

  if (at < 0) return null

  return [ text.slice(0, at), text.slice(at, at + needle.length), text.slice(at + needle.length) ] as const
}
